import { getImageUrl } from './getImageUrl';

const NOTIFICATION_SW_FILE = 'notification-sw.js';
const SW_ACTIVATION_TIMEOUT_MS = 4000;
const DEBUG_PREFIX = '[browserNotifications]';

let notificationRegistrationPromise: Promise<ServiceWorkerRegistration | null> | null = null;
let cachedNotificationIconDataUrl: string | null = null;
let notificationIconLoadPromise: Promise<string | null> | null = null;

export type NotificationTestResult = {
  ok: boolean;
  stage: string;
  permission: NotificationPermission | 'unsupported';
  swUrl?: string;
  swScope?: string;
  swActive?: boolean;
  error?: string;
};

function debugLog(message: string, details?: unknown): void {
  if (details !== undefined) {
    console.info(`${DEBUG_PREFIX} ${message}`, details);
    return;
  }
  console.info(`${DEBUG_PREFIX} ${message}`);
}

function getNotificationBaseUrl(): string {
  const base = import.meta.env.BASE_URL || '/';
  return base.endsWith('/') ? base : `${base}/`;
}

function getNotificationSwUrl(): string {
  return `${getNotificationBaseUrl()}${NOTIFICATION_SW_FILE}`;
}

function isSvgIconUrl(url: string): boolean {
  const normalized = url.split('?')[0].split('#')[0].toLowerCase();
  return normalized.endsWith('.svg') || normalized.startsWith('data:image/svg');
}

function waitForServiceWorkerActivation(
  registration: ServiceWorkerRegistration,
  timeoutMs: number,
): Promise<boolean> {
  if (registration.active) return Promise.resolve(true);

  return new Promise((resolve) => {
    const worker = registration.installing ?? registration.waiting;
    let settled = false;

    const finish = (active: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(active);
    };

    const timeoutId = window.setTimeout(() => finish(Boolean(registration.active)), timeoutMs);

    if (!worker) {
      finish(Boolean(registration.active));
      return;
    }

    const onStateChange = () => {
      if (worker.state === 'activated' || registration.active) {
        finish(true);
      }
    };

    worker.addEventListener('statechange', onStateChange);
    onStateChange();
  });
}

function loadNotificationIconDataUrl(): Promise<string | null> {
  if (cachedNotificationIconDataUrl) return Promise.resolve(cachedNotificationIconDataUrl);
  if (notificationIconLoadPromise) return notificationIconLoadPromise;

  notificationIconLoadPromise = (async () => {
    try {
      const svgUrl = getImageUrl('icon-logo.svg');
      const svgText = await fetch(svgUrl).then((response) => response.text());
      const svgDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;

      return await new Promise<string | null>((resolve) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const size = 128;
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.drawImage(img, 0, 0, size, size);
          cachedNotificationIconDataUrl = canvas.toDataURL('image/png');
          resolve(cachedNotificationIconDataUrl);
        };
        img.onerror = () => resolve(null);
        img.src = svgDataUrl;
      });
    } catch {
      return null;
    }
  })();

  return notificationIconLoadPromise;
}

export function preloadNotificationIcon(): void {
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;
  void loadNotificationIconDataUrl();
}

export async function ensureNotificationRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return null;

  const scope = getNotificationBaseUrl();
  try {
    const existing = await navigator.serviceWorker.getRegistration(scope);
    if (existing?.active) {
      debugLog('using existing service worker', { scope: existing.scope, active: true });
      return existing;
    }
  } catch (error) {
    debugLog('getRegistration failed', error);
  }

  if (notificationRegistrationPromise) return notificationRegistrationPromise;

  notificationRegistrationPromise = (async () => {
    const swUrl = getNotificationSwUrl();
    debugLog('registering service worker', { swUrl, scope });

    try {
      const head = await fetch(swUrl, { method: 'HEAD' });
      if (!head.ok) {
        debugLog('service worker file missing', { swUrl, status: head.status });
        notificationRegistrationPromise = null;
        return null;
      }
    } catch (error) {
      debugLog('service worker HEAD check failed', error);
      notificationRegistrationPromise = null;
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register(swUrl, { scope });
      const activated = await waitForServiceWorkerActivation(registration, SW_ACTIVATION_TIMEOUT_MS);
      debugLog('service worker registered', {
        scope: registration.scope,
        active: Boolean(registration.active),
        activated,
      });
      return registration;
    } catch (error) {
      debugLog('service worker registration failed', error);
      notificationRegistrationPromise = null;
      return null;
    }
  })();

  return notificationRegistrationPromise;
}

export async function prepareBrowserNotifications(): Promise<void> {
  await ensureNotificationRegistration();
  preloadNotificationIcon();
}

export const browserPermissionRequest = (): void => {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') {
    Notification.requestPermission();
  }
};

export const getBrowserNotifStatus = (): NotificationPermission => {
  if (!('Notification' in window)) return 'denied';
  return Notification.permission;
};

export const handleBrowserNotification = async (
  title: string,
  content: string,
  icon?: string | null,
): Promise<boolean> => {
  if (!('Notification' in window)) return false;
  if (Notification.permission !== 'granted') return false;

  const registration = await ensureNotificationRegistration();
  const options: NotificationOptions = { body: content };

  const iconUrl = icon && !isSvgIconUrl(icon) ? icon : cachedNotificationIconDataUrl;
  if (iconUrl) {
    options.icon = iconUrl;
  } else {
    void loadNotificationIconDataUrl();
  }

  if (registration && 'showNotification' in registration) {
    try {
      await registration.showNotification(title, options);
      debugLog('showNotification via service worker succeeded');
      return true;
    } catch (error) {
      debugLog('showNotification via service worker failed', error);
    }
  }

  try {
    const notification = new Notification(title, options);
    notification.onclick = () => {
      window.focus();
    };
    debugLog('Notification constructor succeeded');
    return true;
  } catch (error) {
    debugLog('Notification constructor failed', error);
    try {
      const fallback = new Notification(title, { body: content });
      fallback.onclick = () => {
        window.focus();
      };
      debugLog('Notification constructor fallback (no icon) succeeded');
      return true;
    } catch (fallbackError) {
      debugLog('Notification constructor fallback failed', fallbackError);
      return false;
    }
  }
};

export async function runNotificationTest(title: string, body: string): Promise<NotificationTestResult> {
  debugLog('test started');

  if (!('Notification' in window)) {
    return { ok: false, stage: 'unsupported', permission: 'unsupported' };
  }

  let permission = Notification.permission;
  debugLog('current permission', permission);

  if (permission === 'default') {
    permission = await Notification.requestPermission();
    debugLog('permission after request', permission);
  }

  if (permission !== 'granted') {
    return { ok: false, stage: 'permission-denied', permission };
  }

  const swUrl = getNotificationSwUrl();
  const swScope = getNotificationBaseUrl();
  const registration = await ensureNotificationRegistration();
  const swActive = Boolean(registration?.active);

  const ok = await handleBrowserNotification(title, body);
  const stage = ok ? 'shown' : 'show-failed';

  debugLog('test finished', { ok, stage, swActive });

  return {
    ok,
    stage,
    permission,
    swUrl,
    swScope,
    swActive,
    error: ok ? undefined : stage,
  };
}
